import { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const COOKIE_KEY = 'cookie_preferences';

const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const CookieManager = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(defaultPreferences);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      setTimeout(() => setVisible(true), 1500);
    } else {
      try {
        setPrefs(JSON.parse(stored));
      } catch { }
    }
  }, []);

  const savePrefs = (newPrefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify(newPrefs));
    setPrefs(newPrefs);
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => {
    savePrefs({ necessary: true, analytics: true, marketing: true });
  };

  const rejectAll = () => {
    savePrefs({ necessary: true, analytics: false, marketing: false });
  };

  if (!visible && !showPrefs) return null;

  return (
    <>
      {/* Banner */}
      {visible && !showPrefs && (
        <div
          className="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-auto lg:right-6 lg:max-w-md z-50 bg-white border border-[#E7E5E4] rounded-[24px] shadow-[0_10px_28px_-18px_rgba(0,0,0,0.25)] p-5"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#F5F3EF] flex items-center justify-center shrink-0">
              <Cookie size={16} className="text-[#D8C7A6]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#1F2937] mb-1">Çerez Tercihleri</p>
              <p className="text-xs text-[#5F6B76] leading-relaxed mb-3">
                Deneyiminizi iyileştirmek için çerezler kullanıyoruz. Tercihlerinizi yönetebilirsiniz.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 rounded-full bg-[#1F6E7A] text-white text-xs font-medium hover:bg-[#185960] transition-colors"
                  data-testid="cookie-accept-button"
                >
                  Kabul Et
                </button>
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 rounded-full border border-[#E7E5E4] text-xs font-medium text-[#5F6B76] hover:bg-[#F5F3EF] transition-colors"
                  data-testid="cookie-reject-button"
                >
                  Reddet
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  className="px-4 py-2 rounded-full border border-[#E7E5E4] text-xs font-medium text-[#1F6E7A] hover:bg-[#1F6E7A]/5 transition-colors"
                  data-testid="cookie-manage-button"
                >
                  <Settings size={12} className="inline mr-1" />
                  Yönet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Dialog */}
      <Dialog open={showPrefs} onOpenChange={(open) => { setShowPrefs(open); if (!open) setVisible(false); }}>
        <DialogContent className="max-w-md rounded-[24px]" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Çerez Tercihleri</DialogTitle>
            <DialogDescription className="text-sm text-[#5F6B76]">
              Hangi çerez kategorilerini kabul etmek istediğinizi seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 rounded-2xl bg-[#F5F3EF]">
              <div>
                <p className="text-sm font-medium">Zorunlu Çerezler</p>
                <p className="text-xs text-[#5F6B76]">Sitenin çalışması için gereklidir</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-[#E7E5E4]">
              <div>
                <p className="text-sm font-medium">Analitik Çerezler</p>
                <p className="text-xs text-[#5F6B76]">Site kullanımını analiz etmemize yardımcı olur</p>
              </div>
              <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs({...prefs, analytics: v})} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-[#E7E5E4]">
              <div>
                <p className="text-sm font-medium">Pazarlama Çerezleri</p>
                <p className="text-xs text-[#5F6B76]">Kişisel içerik ve reklamlar için kullanılır</p>
              </div>
              <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs({...prefs, marketing: v})} />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => savePrefs(prefs)}
              className="flex-1 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-medium hover:bg-[#185960] transition-colors"
            >
              Tercihleri Kaydet
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 py-3 rounded-full border border-[#E7E5E4] text-sm font-medium hover:bg-[#F5F3EF] transition-colors"
            >
              Tümünü Kabul Et
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
